// src/components/UserSessionsDialog.tsx
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Stack,
  Switch,
  FormControlLabel,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import LogoutIcon from '@mui/icons-material/Logout';
import ComputerIcon from '@mui/icons-material/Computer';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import TabletIcon from '@mui/icons-material/Tablet';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import { useTranslation } from 'react-i18next';
import { ManagedUser, AdminUserSession } from '../types/auth';
import { getUserSessions, revokeAllUserSessions, revokeUserSession } from '../services/userService';

interface UserSessionsDialogProps {
  open: boolean;
  onClose: () => void;
  user: ManagedUser;
}

const UserSessionsDialog: React.FC<UserSessionsDialogProps> = ({ open, onClose, user }) => {
  const { t } = useTranslation();
  const [sessions, setSessions] = useState<AdminUserSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeExpired, setIncludeExpired] = useState(false);
  const [totalActive, setTotalActive] = useState(0);
  const [totalExpired, setTotalExpired] = useState(0);
  const [actionLoading, setActionLoading] = useState<number | 'all' | null>(null);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getUserSessions(user.id, includeExpired);
      setSessions(response.sessions);
      setTotalActive(response.total_active);
      setTotalExpired(response.total_expired);
    } catch (err: any) {
      console.error('Failed to fetch sessions:', err);
      setError(err.response?.data?.detail || t('superadmin.sessions.errorFetching'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchSessions();
    }
  }, [open, includeExpired, user.id]);

  const handleRevokeSession = async (sessionId: number) => {
    try {
      setActionLoading(sessionId);
      await revokeUserSession(user.id, sessionId);
      fetchSessions();
    } catch (err: any) {
      console.error('Failed to revoke session:', err);
      setError(err.response?.data?.detail || t('superadmin.sessions.errorRevoking'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleRevokeAll = async () => {
    try {
      setActionLoading('all');
      await revokeAllUserSessions(user.id);
      fetchSessions();
    } catch (err: any) {
      console.error('Failed to revoke all sessions:', err);
      setError(err.response?.data?.detail || t('superadmin.sessions.errorRevoking'));
    } finally {
      setActionLoading(null);
    }
  };

  const getDeviceIcon = (userAgent: string) => {
    const ua = userAgent.toLowerCase();
    if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) {
      return <PhoneAndroidIcon />;
    }
    if (ua.includes('tablet') || ua.includes('ipad')) {
      return <TabletIcon />;
    }
    if (ua.includes('windows') || ua.includes('mac') || ua.includes('linux')) {
      return <ComputerIcon />;
    }
    return <DevicesOtherIcon />;
  };

  const getPlatform = (session: AdminUserSession): string => {
    if (session.device_info?.platform) {
      return session.device_info.platform;
    }
    const ua = session.user_agent.toLowerCase();
    if (ua.includes('android')) return 'Android';
    if (ua.includes('iphone') || ua.includes('ipad')) return 'iOS';
    if (ua.includes('windows')) return 'Windows';
    if (ua.includes('mac')) return 'macOS';
    if (ua.includes('linux')) return 'Linux';
    return t('superadmin.sessions.unknown');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6">
            {t('superadmin.sessions.title')} - {user.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user.email}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {/* Stats and Controls */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Stack direction="row" spacing={2}>
            <Chip
              label={`${totalActive} ${t('superadmin.sessions.active')}`}
              color="success"
              variant="outlined"
            />
            <Chip
              label={`${totalExpired} ${t('superadmin.sessions.expired')}`}
              color="default"
              variant="outlined"
            />
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={includeExpired}
                  onChange={(e) => setIncludeExpired(e.target.checked)}
                  size="small"
                />
              }
              label={t('superadmin.sessions.includeExpired')}
            />
            <Button
              variant="outlined"
              color="error"
              startIcon={<LogoutIcon />}
              onClick={handleRevokeAll}
              disabled={actionLoading !== null || totalActive === 0}
              size="small"
            >
              {actionLoading === 'all' ? (
                <CircularProgress size={20} />
              ) : (
                t('superadmin.sessions.revokeAll')
              )}
            </Button>
          </Stack>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : sessions.length > 0 ? (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 700 }}>{t('superadmin.sessions.device')}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('superadmin.sessions.ipAddress')}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('superadmin.sessions.status')}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('superadmin.sessions.createdAt')}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('superadmin.sessions.lastUsed')}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t('superadmin.sessions.expiresAt')}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">
                    {t('superadmin.sessions.actions')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sessions.map((session) => (
                  <TableRow
                    key={session.id}
                    sx={{
                      '&:hover': { bgcolor: 'grey.50' },
                      opacity: session.is_active ? 1 : 0.6,
                    }}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Tooltip title={session.user_agent}>
                          <Box sx={{ color: 'text.secondary' }}>
                            {getDeviceIcon(session.user_agent)}
                          </Box>
                        </Tooltip>
                        <Typography variant="body2">{getPlatform(session)}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {session.ip_address}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {session.is_active ? (
                        <Chip
                          label={t('superadmin.sessions.active')}
                          color="success"
                          size="small"
                        />
                      ) : (
                        <Chip
                          label={t('superadmin.sessions.expired')}
                          color="default"
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{formatDate(session.created_at)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {session.last_used_at ? formatDate(session.last_used_at) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{formatDate(session.expires_at)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      {session.is_active && (
                        <Tooltip title={t('superadmin.sessions.revoke')}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRevokeSession(session.id)}
                            disabled={actionLoading !== null}
                          >
                            {actionLoading === session.id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <DeleteIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
            <Typography variant="body2" color="text.secondary">
              {t('superadmin.sessions.noSessions')}
            </Typography>
          </Paper>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserSessionsDialog;
